import { type JSX, useState } from 'react';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { Button } from 'Components/Buttons/Buttons';
import { AssociatedDocumentsModal } from 'Components/Modal/AssociatedDocumentsModal/AssociatedDocumentsModal';
import { Modal } from 'Components/Modal/Modal';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { Table } from 'Components/Table/Table';
import { ICON_CLOSE } from 'Config/Icons';
import { UploadedFile } from 'Models/Files';
import { FolderVersion } from 'Models/Folders';

import { FolderVersionSnapshot } from './FolderVersionSnapshot';

export interface FolderVersionHistoryTableProps {
    documentApi: DocumentApi;
    versions: FolderVersion[];
}

/**
 * Displays a table of historical `FolderVersion`s.
 * Sorting is disabled. The table presents a chronological history; the newest version is always listed first.
 * Clicking a version's date opens a modal containing the full details of that version.
 */
export const FolderVersionHistoryTable = (props: FolderVersionHistoryTableProps): JSX.Element => {
    const [displayedDocuments, setDisplayedDocuments] = useState<UploadedFile[]>();
    const [versionToShowInModal, setVersionToShowInModal] = useState<FolderVersion>();

    const columns: ColumnConfig<FolderVersion>[] = [
        {
            type: ColumnType.DATE,
            name: 'Date',
            getDateString: (version) => version.last_updated,
            getOnClickTextAction: () => ({ execute: (version) => setVersionToShowInModal(version) }),
        },
        {
            type: ColumnType.STRING,
            name: 'Folder Type',
            getString: (version) => version.type,
        },
        {
            type: ColumnType.MULTIPLE_FILES,
            name: 'Documents',
            getFiles: (version) => version.files ?? [],
            onClick: (files) => setDisplayedDocuments(files),
        },
        {
            type: ColumnType.DATE,
            name: 'Effective Date',
            getDateString: (version) => version.effective_date,
        },
        {
            type: ColumnType.DATE,
            name: 'Expiration Date',
            getDateString: (version) => version.expiration_date,
        },
    ];

    return (
        <>
            {displayedDocuments && <AssociatedDocumentsModal associatedDocuments={displayedDocuments} documentApi={props.documentApi} hideModal={() => setDisplayedDocuments(undefined)} />}
            {versionToShowInModal && (
                <Modal open header={versionToShowInModal.name}>
                    <FolderVersionSnapshot documentApi={props.documentApi} version={versionToShowInModal} />
                    <div className={'modalFormButtonContainer'}>
                        <Button variant="secondary" onClick={() => setVersionToShowInModal(undefined)} fontAwesomeImage={ICON_CLOSE}>
                            Close
                        </Button>
                    </div>
                </Modal>
            )}
            <Table<FolderVersion> columns={columns} defaultSortColumn="Date" defaultSortDirection="descending" disableSorting filters={[]} getUniqueIdForRow={(version) => version.id} rowsData={props.versions} />
        </>
    );
};
